import ky, { HTTPError } from 'ky';
import type { ZodType } from 'zod';
import { env } from '@shared/config/env';
import { getToken, hardLogout } from '@shared/auth';
import { AppError } from './AppError';
import { normalizeError } from './normalizeError';

/**
 * Pull the backend's own error message out of a failed response so the user sees WHY (e.g.
 * "currency_id is required" / a 422 field error) instead of a generic string. Falls back to
 * `normalizeError` when the body can't be read or isn't the expected envelope.
 */
async function normalizeWithServerMessage(error: unknown): Promise<AppError> {
  if (error instanceof HTTPError) {
    try {
      const body = (await error.response.clone().json()) as {
        message?: string;
        data?: { errors?: { message?: string }[] };
      };
      const fieldError = body.data?.errors?.find((e) => e.message)?.message;
      const message = fieldError ?? body.message;
      if (message) {
        const status = error.response.status;
        return new AppError({
          // ONLY 401 is 'auth' (session torn down + redirect, so the global handler stays
          // silent). A 403 is a PERMISSION rejection the user must see — e.g. a member demoted
          // to view-only mid-edit gets "You cannot change wallet entries in this group…" from
          // the backend; classing it 'auth' used to swallow that toast entirely.
          kind: status === 401 ? 'auth' : 'business',
          status,
          messageForUser: message,
          cause: error,
        });
      }
    } catch {
      // Body wasn't JSON / already consumed — fall through to the generic mapping.
    }
  }
  return normalizeError(error);
}

/**
 * All requests go directly to the backend (Riloma-API). The SPA holds the JWT in localStorage
 * and sends it as `Authorization: Bearer <token>`. There is no BFF and no cookie session.
 *
 * NOTE: this is the single raw transport. Everything else MUST import typed entity APIs,
 * not this module (enforced by eslint import-x/no-restricted-paths).
 */
const PREFIX =
  env.VITE_API_BASE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');

export const api = ky.create({
  prefixUrl: PREFIX,
  timeout: 20_000,
  // Never let the browser do conditional (ETag/If-None-Match) revalidation on these calls: a
  // bare 304 can reach fetch() with an empty body (no cached copy to merge it with, e.g. after a
  // hard refresh), which blows up JSON parsing as an unrecognized error. React Query already owns
  // caching here (staleTime), so the HTTP layer doesn't need its own.
  cache: 'no-store',
  // Retry idempotent reads on transient server errors only — NEVER 4xx, NEVER mutations.
  retry: { limit: 2, methods: ['get'], statusCodes: [500, 502, 503, 504] },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = getToken();
        if (token) request.headers.set('authorization', `Bearer ${token}`);
        request.headers.set('x-request-id', crypto.randomUUID());
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        if (response.status !== 401) return response;
        // No refresh endpoint exists — an expired/invalid token means re-authenticate.
        await hardLogout();
        throw new AppError({
          kind: 'auth',
          messageForUser: 'Your session expired. Please sign in again.',
        });
      },
    ],
  },
});

/**
 * Same transport WITHOUT the 401 redirect interceptor — for auth probes where a 401 is an
 * answer ("not signed in"), not a failure to recover.
 */
export const apiPublic = ky.create({
  prefixUrl: PREFIX,
  timeout: 20_000,
  cache: 'no-store',
});

/**
 * GET + runtime-validate the response against a Zod schema (boundary validation). Pass
 * `searchParams` to attach query params — ky serializes them reliably (preferred over hand-built
 * query strings).
 */
export async function getValidated<T>(
  url: string,
  schema: ZodType<T>,
  searchParams?: Record<string, string | number | boolean>,
): Promise<T> {
  try {
    const response = await (searchParams ? api.get(url, { searchParams }) : api.get(url));
    return schema.parse(await response.json<unknown>());
  } catch (error) {
    throw normalizeError(error);
  }
}

/** Like getValidated, but via the public (no-redirect) client. */
export async function getValidatedPublic<T>(url: string, schema: ZodType<T>): Promise<T> {
  try {
    return schema.parse(await apiPublic.get(url).json<unknown>());
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Send a mutating request (POST/PUT/PATCH/DELETE) + validate the response. Failed responses
 * surface the backend's own envelope message (like `sendFormValidated`) so business rejections —
 * e.g. deleting an expense/settlement tied to a deleted user — toast the real reason instead of
 * the generic status text (mobile parity).
 */
export async function sendValidated<T>(
  method: 'post' | 'put' | 'patch' | 'delete',
  url: string,
  body: unknown,
  schema: ZodType<T>,
): Promise<T> {
  try {
    return schema.parse(await api[method](url, { json: body }).json<unknown>());
  } catch (error) {
    throw await normalizeWithServerMessage(error);
  }
}

/**
 * Send a `multipart/form-data` mutation + validate the response. Some backend endpoints (e.g.
 * `pending-expense`) expect form-data even when no binary is attached, with every value
 * stringified and nested keys bracketed (`paid_by[0][id]`). Pass those already-encoded entries as
 * `fields`; `Blob`/`File` values are appended as-is. ky sets the multipart boundary itself — do NOT
 * set Content-Type (the JSON default header is dropped automatically for FormData bodies).
 */
export async function sendFormValidated<T>(
  method: 'post' | 'put' | 'patch',
  url: string,
  fields: Record<string, string | Blob>,
  schema: ZodType<T>,
): Promise<T> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) form.append(key, value);
  try {
    return schema.parse(await api[method](url, { body: form }).json<unknown>());
  } catch (error) {
    throw await normalizeWithServerMessage(error);
  }
}
